// External imports
import { useContext, useEffect, useState } from "react";

import { SearchOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Grid,
  InputBase,
  LinearProgress,
  Link,
  Pagination,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import {
  OrgConfigModel,
  OrgConfigNotifyItem,
  OrgConfigStatusNotifyGroup,
  getOrgConfigPortal,
  patchOrgConfig,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useNavigate, useParams, useLocation } from "react-router";

import { mailTo, validateEmail } from "@/common/utils";
import { DisabledComponentDialog } from "@/components/DisabledComponent";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import { IUserPermission } from "@/types/typesAlerts";

import { OnlineReportingContext } from "./context";
import EmailTemplateConfig from "./EmailTemplateConfig";
import styles from "./ReportV2Settings.module.css";

interface IAddEmailOption {
  inputValue: string;
  label: string;
}

type ActionTabKey = "initial" | "approved" | "rejected" | "escalated";

const ACTION_TABS: { key: ActionTabKey; label: string }[] = [
  { key: "initial", label: "Initial Submission" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "escalated", label: "Escalated" },
];

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.black, 0.05),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.black, 0.1),
  },
  marginRight: theme.spacing(2),
  marginBottom: theme.spacing(2),
  width: "100%",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  padding: theme.spacing(1, 1, 1, 0),
  paddingLeft: `calc(1em + ${theme.spacing(4)})`,
}));

const ReportV2Settings = () => {
  const [serviceConfig, setServiceConfig] = useState<OrgConfigModel | null>(
    null,
  );
  const { reportConfigurations, fetchReportConfigurations } = useContext(
    OnlineReportingContext,
  );
  const { currentUser } = useContext(AuthStateContext);
  const [reportPermissions] = useState(
    currentUser &&
      parseUserPermissions(currentUser?.permissions as IUserPermission[]),
  );
  const location = useLocation();
  const isIntelMode = location.pathname.includes("/rubiintel/");
  // Check intel permissions when in intel mode, otherwise check report_v2 permissions
  const canViewSettingsRV2 = reportPermissions
    ? isIntelMode
      ? reportPermissions.canViewSettingsIntel
      : reportPermissions.canViewSettingsRV2
    : true;
  const navigate = useNavigate();
  const { orgId } = useParams();
  const { isDevEnv } = useDevEnvironment();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;
  const [focusedItem, setFocusedItem] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<ActionTabKey>("initial");

  useEffect(() => {
    async function getConfig() {
      const response = (
        await getOrgConfigPortal({ path: { org_id: orgId ?? "" } })
      ).data!;
      setServiceConfig(response);
    }
    getConfig();
  }, [orgId]);

  useEffect(() => {
    fetchReportConfigurations(orgId ?? "");
  }, [orgId]);

  useEffect(() => {
    if (serviceConfig && reportConfigurations) {
      const existingKeys = serviceConfig.notify?.notify_items.map(
        (item) => item.key,
      );

      const newConfigs = reportConfigurations.filter(
        (config) => !existingKeys?.includes(config.survey_class),
      );

      const existingPerReportGroups =
        serviceConfig.notify?.per_report_status_notify_items;
      const outcomeKeys = ACTION_TABS.filter((t) => t.key !== "initial").map(
        (t) => t.key,
      );
      const perReportGroupsSeeded = outcomeKeys.every((status) =>
        existingPerReportGroups?.some((g) => g.status === status),
      );
      const allSurveyClassesCovered =
        !!existingPerReportGroups &&
        existingPerReportGroups.every(
          (g) =>
            g.items.some((i) => i.key === "all") &&
            (reportConfigurations ?? []).every((c) =>
              g.items.some((i) => i.key === c.survey_class),
            ),
        );
      const initialAllSeeded = existingKeys?.includes("all") ?? false;

      const existingStatusItems = serviceConfig.notify?.status_notify_items;
      const legacyOutcomeEmails = outcomeKeys.map((status) => ({
        status,
        emails:
          existingStatusItems?.find((i) => i.key === status)?.emails ?? [],
      }));
      const hasLegacyOutcomeEmailsToMigrate = legacyOutcomeEmails.some(
        (l) => l.emails.length > 0,
      );

      if (
        newConfigs.length === 0 &&
        perReportGroupsSeeded &&
        allSurveyClassesCovered &&
        initialAllSeeded &&
        !hasLegacyOutcomeEmailsToMigrate
      ) {
        return;
      }

      const newNotifyItems = newConfigs.map((config) => ({
        key: config.survey_class,
        emails: [],
      }));

      const updatedNotifyItems = [
        ...(initialAllSeeded ? [] : [{ key: "all", emails: [] }]),
        ...(serviceConfig.notify?.notify_items || []),
        ...newNotifyItems,
      ];

      const statusNotifyItems = (
        existingStatusItems || [
          { key: "all", emails: [] },
          { key: "approved", emails: [] },
          { key: "rejected", emails: [] },
          { key: "escalated", emails: [] },
          { key: "modification_requested", emails: [] },
          { key: "supplemental_approved", emails: [] },
        ]
      ).map((item) =>
        outcomeKeys.includes(item.key as ActionTabKey)
          ? { ...item, emails: [] }
          : item,
      );

      const perReportStatusNotifyItems: OrgConfigStatusNotifyGroup[] =
        outcomeKeys.map((status) => {
          const existingGroup = existingPerReportGroups?.find(
            (g) => g.status === status,
          );
          const existingItems = existingGroup?.items ?? [];
          const existingItemKeys = new Set(existingItems.map((i) => i.key));
          const missingItems = reportConfigurations
            .filter((c) => !existingItemKeys.has(c.survey_class))
            .map((c) => ({ key: c.survey_class, emails: [] }));
          const itemsWithMissing = [...existingItems, ...missingItems];

          const hasAllItem = itemsWithMissing.some((i) => i.key === "all");
          const legacyEmails =
            legacyOutcomeEmails.find((l) => l.status === status)?.emails ?? [];

          let finalItems = hasAllItem
            ? itemsWithMissing
            : [{ key: "all", emails: [] }, ...itemsWithMissing];

          if (legacyEmails.length > 0) {
            finalItems = finalItems.map((i) =>
              i.key === "all"
                ? {
                    ...i,
                    emails: Array.from(new Set([...i.emails, ...legacyEmails])),
                  }
                : i,
            );
          }

          return { status, items: finalItems };
        });

      setServiceConfig((prev) => ({
        ...prev!,
        notify: {
          notify_items: updatedNotifyItems,
          status_notify_items: statusNotifyItems,
          per_report_status_notify_items: perReportStatusNotifyItems,
        },
      }));
    }
  }, [serviceConfig, reportConfigurations]);

  type EmailScope =
    | { type: "initial" }
    | { type: "status_global" }
    | { type: "per_report_status"; status: string };

  const handleEmailChange = async (
    key: string,
    newValues: (string | IAddEmailOption)[],
    scope: EmailScope,
  ) => {
    if (!serviceConfig) return;

    const emails = newValues.map((v) =>
      typeof v === "string" ? v : v.inputValue,
    );
    if (!emails.every((email) => validateEmail(email))) {
      toast.error("Invalid email format!");
      return;
    }

    let updatedNotifyItems = serviceConfig.notify?.notify_items ?? [];
    let updatedStatusNotifyItems =
      serviceConfig.notify?.status_notify_items ?? [];
    let updatedPerReportStatusNotifyItems =
      serviceConfig.notify?.per_report_status_notify_items ?? [];

    if (scope.type === "initial") {
      updatedNotifyItems = updatedNotifyItems.map((item) =>
        item.key === key ? { ...item, emails } : item,
      );
    } else if (scope.type === "status_global") {
      updatedStatusNotifyItems = updatedStatusNotifyItems.map((item) =>
        item.key === key ? { ...item, emails } : item,
      );
    } else {
      updatedPerReportStatusNotifyItems = updatedPerReportStatusNotifyItems.map(
        (group) =>
          group.status === scope.status
            ? {
                ...group,
                items: group.items.map((item) =>
                  item.key === key ? { ...item, emails } : item,
                ),
              }
            : group,
      );
    }

    const newConfig = (
      await patchOrgConfig({
        path: { org_id: orgId ?? "" },
        body: {
          notify: {
            notify_items: updatedNotifyItems,
            status_notify_items: updatedStatusNotifyItems,
            per_report_status_notify_items: updatedPerReportStatusNotifyItems,
          },
        },
      })
    ).data!;
    setServiceConfig(newConfig);
  };

  const matchesSearch = (item: OrgConfigNotifyItem) =>
    item.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reportConfigurations
      ?.find((config) => config.survey_class === item.key)
      ?.name.toLowerCase()
      .includes(searchTerm.toLowerCase());

  const perReportItemsForTab = (tab: ActionTabKey): OrgConfigNotifyItem[] => {
    const items =
      tab === "initial"
        ? (serviceConfig?.notify?.notify_items ?? [])
        : (serviceConfig?.notify?.per_report_status_notify_items?.find(
            (g) => g.status === tab,
          )?.items ?? []);
    return items.filter((item) => item.key !== "all");
  };

  const globalItemForTab = (
    tab: ActionTabKey,
  ): OrgConfigNotifyItem | undefined => {
    if (tab === "initial") {
      return serviceConfig?.notify?.notify_items?.find((i) => i.key === "all");
    }
    return serviceConfig?.notify?.per_report_status_notify_items
      ?.find((g) => g.status === tab)
      ?.items.find((i) => i.key === "all");
  };

  const globalScopeForTab = (tab: ActionTabKey): EmailScope =>
    tab === "initial"
      ? { type: "initial" }
      : { type: "per_report_status", status: tab };

  const filteredNotifyItems =
    perReportItemsForTab(selectedTab).filter(matchesSearch);

  const paginatedItems = filteredNotifyItems.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  const tabConfiguredCount = (tab: ActionTabKey): number => {
    const perReport = perReportItemsForTab(tab).filter(
      (i) => i.emails.length > 0,
    ).length;
    const global = globalItemForTab(tab)?.emails.length ?? 0;
    return perReport + (global > 0 ? 1 : 0);
  };

  const scopeKey = (scope: EmailScope): string => {
    if (scope.type === "initial") return "initial";
    if (scope.type === "status_global") return "status_global";
    return `per_report_status:${scope.status}`;
  };

  const renderNotifyItem = (item: OrgConfigNotifyItem, scope: EmailScope) => {
    const config = reportConfigurations?.find(
      (config) => config.survey_class === item.key,
    );
    const focusKey = item.key + scopeKey(scope);

    const labelForKey = () => {
      if (scope.type === "status_global") {
        if (item.key === "all") return "ANY ACTION";
        if (item.key === "modification_requested") return "Awaiting Response";
        if (item.key === "supplemental_approved")
          return "Supplemental Approved";
        return "ALL REPORT TYPES";
      }
      if (config?.name) return config.name;
      if (item.key === "all") return "ALL REPORT TYPES";
      return item.key;
    };

    return (
      <Box
        key={focusKey}
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          width: "100%",
          padding: "4px",
        }}
      >
        <Grid
          size={3}
          sx={{
            display: "flex",
            alignItems: "center",
            width: "30%",
            marginTop: "8px",
            marginRight: "8px",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              width: "100%",
              textOverflow: "ellipsis",
            }}
          >
            {labelForKey()}
          </Typography>
        </Grid>
        <Grid
          size={9}
          sx={{
            width: "100%",
          }}
        >
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            id={`email-autocomplete-${focusKey}`}
            value={item.emails}
            onChange={(event, newValues) =>
              handleEmailChange(item.key, newValues, scope)
            }
            sx={{ width: "100%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                label="Emails"
                placeholder="Add email..."
                helperText={
                  focusedItem === focusKey ? "Press enter to save" : ""
                }
                sx={{ width: "100%" }}
                onFocus={() => setFocusedItem(focusKey)}
                onBlur={() => setFocusedItem(null)}
              />
            )}
          />
        </Grid>
      </Box>
    );
  };

  if (!canViewSettingsRV2) {
    return (
      <DisabledComponentDialog
        isDialogOpen={true}
        handleClose={() => {
          navigate(`/`);
        }}
        title="You do not have access to view report settings."
        message={
          <Typography>
            Please contact{" "}
            <Link
              rel="noopener noreferrer"
              target="_blank"
              href={mailTo({
                email: "support@tryrubicon.com",
                subject: "Requesting Access to View Reports",
                body: `Hello Rubicon Support Team, 

                I would like to request access to view report settings.

                Thank you. 

                Sincerely,

                ${currentUser?.first_name} ${currentUser?.last_name} 
                ${currentUser?.email}`,
              })}
              color="secondary"
            >
              support@tryrubicon.com
            </Link>{" "}
            to request access.
          </Typography>
        }
      />
    );
  }

  const globalItem = globalItemForTab(selectedTab);

  const tabStatusKeys = new Set(
    ACTION_TABS.filter((t) => t.key !== "initial").map((t) => t.key as string),
  );
  const otherStatusItems =
    serviceConfig?.notify?.status_notify_items?.filter(
      (item) => !tabStatusKeys.has(item.key),
    ) ?? [];

  const tabDescription =
    selectedTab === "initial"
      ? "Manage who receives an email notification when a new report is submitted. Notifications can be sent for all report types or only specific report categories."
      : `Manage who receives an email notification when a report is ${selectedTab}. Notifications can be sent for all report types or only specific report categories.`;

  return (
    <div className={styles.formContainer}>
      <Paper elevation={3} className={styles.sectionContainer}>
        <Typography variant="h6">
          <b>Notification Settings</b>
        </Typography>

        <Tabs
          value={selectedTab}
          onChange={(_e, value) => {
            setSelectedTab(value);
            setPage(1);
          }}
          sx={{ marginBottom: 2 }}
        >
          {ACTION_TABS.map((tab) => {
            const count = tabConfiguredCount(tab.key);
            return (
              <Tab
                key={tab.key}
                value={tab.key}
                label={
                  <Badge
                    color="primary"
                    badgeContent={count}
                    invisible={count === 0}
                    sx={{ paddingRight: count > 0 ? 2 : 0 }}
                  >
                    {tab.label}
                  </Badge>
                }
              />
            );
          })}
        </Tabs>

        <Typography variant="body2" gutterBottom>
          {tabDescription}
        </Typography>

        {globalItem && (
          <Box sx={{ width: "100%", marginBottom: 2 }}>
            {renderNotifyItem(globalItem, globalScopeForTab(selectedTab))}
          </Box>
        )}

        <Search>
          <SearchIconWrapper>
            <SearchOutlined />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder="Filter by report name..."
            inputProps={{ "aria-label": "search" }}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Search>

        {!serviceConfig ? (
          <LinearProgress sx={{ width: "100%" }} />
        ) : (
          <Box sx={{ width: "100%" }}>
            {paginatedItems.map((item) =>
              renderNotifyItem(
                item,
                selectedTab === "initial"
                  ? { type: "initial" }
                  : { type: "per_report_status", status: selectedTab },
              ),
            )}
            <Pagination
              count={Math.ceil(filteredNotifyItems.length / itemsPerPage)}
              page={page}
              onChange={(event, value) => setPage(value)}
              sx={{ marginTop: 2, display: "flex", justifyContent: "center" }}
            />
          </Box>
        )}

        {otherStatusItems.length > 0 && (
          <>
            <Typography variant="body2" gutterBottom sx={{ marginTop: 3 }}>
              <b>Report Action Notification</b>
            </Typography>
            <Typography variant="body2" gutterBottom>
              Manage who receives an email notification for other report actions
              that apply across all report types.
            </Typography>
            <Box sx={{ width: "100%" }}>
              {otherStatusItems.map((item) =>
                renderNotifyItem(item, { type: "status_global" }),
              )}
            </Box>
          </>
        )}
      </Paper>

      <Paper elevation={3} className={styles.sectionContainer}>
        <EmailTemplateConfig />
      </Paper>

      {isDevEnv && (
        <Paper elevation={3} className={styles.sectionContainer}>
          <Typography variant="h6">
            <b>Advanced Notifications</b>
          </Typography>
          <Typography variant="body2" gutterBottom>
            Route status-change emails to specific recipients based on the
            submission&apos;s status and field values.
          </Typography>
          <Box>
            <Button
              variant="outlined"
              onClick={() => navigate("advanced-notifications")}
            >
              Open Advanced Notifications
            </Button>
          </Box>
        </Paper>
      )}
    </div>
  );
};

export default ReportV2Settings;
